import { Button, Card, Col, Row, Tag, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AotvLogo } from '../components/AotvLogo';
import { PageHead } from '../components/PageHead';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

const { Title, Text } = Typography;

type MyEvent = {
  id: string;
  title: string;
  date: string | null;
  status: 'draft' | 'live' | 'ended';
};

export function SpeakerLandingScene() {
  const { session, setSession } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState<MyEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchEvents = async () => {
      try {
        const rows = await api<MyEvent[]>('/api/me/events');
        if (!cancelled) setEvents(rows);
      } catch (err) {
        if (!cancelled) {
          message.error((err as { error?: string }).error ?? 'Failed to load events');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetchEvents();
    const onFocus = () => void fetchEvents();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const signOut = () => {
    setSession(null);
    navigate('/login', { replace: true });
  };

  return (
    <div className="auth-container">
      <PageHead title="My events" />
      <Row justify="center">
        <Col xs={22} sm={20} md={16} lg={12} xl={10}>
          <div className="auth-logo">
            <AotvLogo />
          </div>
          <Card>
            <Title level={3} style={{ marginTop: 0 }}>
              {events.length > 1 ? 'Pick the event you\'re attending' : 'Your events'}
            </Title>
            <Text type="secondary">
              Signed in as <strong>{session?.email}</strong>.{' '}
              <a onClick={signOut} style={{ cursor: 'pointer' }}>
                Sign out
              </a>
            </Text>
            <div style={{ marginTop: 24 }}>
              {loading ? (
                <Text>Loading…</Text>
              ) : events.length === 0 ? (
                <Text type="secondary">
                  You're not on any AOTV Call events yet. If you were expecting
                  to be, the team should have invited you via email.
                </Text>
              ) : (
                events.map((ev) => (
                  <Card
                    key={ev.id}
                    size="small"
                    style={{ marginBottom: 12, cursor: 'pointer' }}
                    onClick={() => navigate(`/waiting/${ev.id}`)}
                  >
                    <Row align="middle" gutter={12}>
                      <Col flex="auto">
                        <Text strong style={{ display: 'block' }}>
                          {ev.title}
                        </Text>
                        <Text type="secondary">
                          {ev.date ? dayjs(ev.date).format('dddd, D MMMM YYYY') : 'No date set'}
                        </Text>
                      </Col>
                      <Col>
                        <Tag
                          color={
                            ev.status === 'live'
                              ? 'green'
                              : ev.status === 'ended'
                                ? 'default'
                                : 'blue'
                          }
                        >
                          {ev.status}
                        </Tag>
                      </Col>
                      <Col>
                        <Link to={`/waiting/${ev.id}`}>
                          <Button type="primary">Enter</Button>
                        </Link>
                      </Col>
                    </Row>
                  </Card>
                ))
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
